/**
 * Piege de focus des fenetres modales (motif « dialog (modal) » de l'ARIA APG) : Tab sur le dernier element atteignable
 * revient au premier, Maj+Tab sur le premier va au dernier, et un focus egare hors de la fenetre (clic sur le voile,
 * barre de titre) y rentre. Sans lui, `aria-modal="true"` n'est qu'une promesse : la tabulation sort de la fenetre du
 * mot de passe maitre ou du consentement et agit sur le coffre cache dessous.
 *
 * Pur et sans DOM pour la decision (`modalTabTarget`) ; `modalFocusables` liste les elements atteignables dans l'ordre.
 */

/** Elements qu'atteint la tabulation (ordre du document), hors `tabindex="-1"` et elements desactives. */
export const MODAL_FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

/** Elements atteignables au clavier dans la fenetre, ceux d'une partie cachee (`hidden`, `inert`) exclus. */
export function modalFocusables(dialog: Element): HTMLElement[] {
  return Array.from(dialog.querySelectorAll<HTMLElement>(MODAL_FOCUSABLE_SELECTOR)).filter(
    (element) => element.closest('[hidden], [inert]') === null && element.tabIndex >= 0,
  );
}

/**
 * Ou poser le focus pour une tabulation dans une fenetre modale : l'element qui boucle, ou `null` quand le navigateur
 * peut avancer seul (focus au milieu de la fenetre).
 */
export function modalTabTarget<T>(focusables: readonly T[], active: T | null, backward: boolean): T | null {
  const first = focusables[0];
  const last = focusables[focusables.length - 1];
  if (first === undefined || last === undefined) return null;
  const index = active === null ? -1 : focusables.indexOf(active);
  // Focus hors de la fenetre : il y rentre par le bord d'ou vient la tabulation.
  if (index === -1) return backward ? last : first;
  if (backward && index === 0) return last;
  if (!backward && index === focusables.length - 1) return first;
  return null;
}

/** Fenetre ouverte et l'endroit d'ou elle a ete ouverte, pour y rendre le focus a sa fermeture. */
export interface ModalReturn<T> {
  readonly key: string;
  readonly origin: T | null;
}

/**
 * Retour du focus a la fermeture d'une fenetre modale (motif « dialog (modal) » de l'ARIA APG : le focus revient sur
 * l'element qui l'a ouverte). La vue est re-rendue en entier : on compare les fenetres ouvertes avant (`before`) et apres
 * (`after`) le rendu. Une fenetre qui apparait retient `origin` (le focus juste avant le rendu) ; une fenetre qui
 * disparait rend le sien (`returnTo`). Plusieurs fermees d'un coup : l'origine de la plus ancienne, la seule hors d'elles.
 * Une fenetre qui en remplace une autre herite de l'origine de la fermee (le focus d'avant etait dans celle-ci).
 */
export function modalReturnPlan<T>(
  stack: readonly ModalReturn<T>[],
  before: readonly string[],
  after: readonly string[],
  origin: T | null,
): { stack: ModalReturn<T>[]; returnTo: T | null } {
  const closed = stack.filter((record) => before.includes(record.key) && !after.includes(record.key));
  const returnTo = closed[0]?.origin ?? null;
  const kept = stack.filter((record) => after.includes(record.key));
  const opened = after.filter((key) => !before.includes(key) && !kept.some((record) => record.key === key));
  const openedOrigin = closed.length > 0 ? returnTo : origin;
  return {
    stack: [...kept, ...opened.map((key) => ({ key, origin: openedOrigin }))],
    returnTo: opened.length > 0 ? null : returnTo,
  };
}

/** Fenetre modale du dessus dans `scope` : la derniere rendue, ou `null` si aucune n'est ouverte. */
export function topModal(scope: ParentNode): HTMLElement | null {
  const dialogs = scope.querySelectorAll<HTMLElement>('[aria-modal="true"]');
  return dialogs[dialogs.length - 1] ?? null;
}

/**
 * Arriere-plan d'une fenetre modale rendu `inert` (motif « dialog (modal) » de l'ARIA APG) : le coffre dessous, les
 * fenetres empilees sous celle du dessus et le chrome (`chrome` : barre de titre, barre d'etat) ne sont plus ni
 * cliquables, ni atteignables au focus, ni lus par le curseur virtuel d'un lecteur d'ecran -- `aria-modal` seul n'est
 * qu'une indication que plusieurs lecteurs ignorent, et la souris passe la ou le voile ne couvre pas. Les couches de la
 * vue sont ses enfants directs ; sans fenetre ouverte, tout redevient actif.
 */
export function setModalBackgroundInert(view: Element, chrome: readonly Element[]): void {
  let layer: Element | null = topModal(view);
  while (layer !== null && layer.parentElement !== view) layer = layer.parentElement;
  for (const element of [...chrome, ...Array.from(view.children)]) element.toggleAttribute('inert', layer !== null && element !== layer);
}

/** Nom stable d'une fenetre modale d'un rendu a l'autre : sa classe `*-panel`. */
export function modalKey(dialog: Element): string {
  return Array.from(dialog.classList).find((name) => name.endsWith('-panel')) ?? dialog.getAttribute('aria-labelledby') ?? '';
}
